import { faker } from '@faker-js/faker';
import type {
  ConfigurationModuleSnapshotResourceParameterSetReadBffV2Api,
  ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api,
  EnumLogicalDeviceRoleApi,
  ParameterSetSnapshotComputedResourceReadBffV2Api,
  ParameterSetValueItemBffV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumParameterSetFieldValueTypeV2 } from '@ingenico/api-sdk-device-management-private-v2';
import type { HttpRequestResolverExtras, PathParams, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';
import { generateConfigurationModuleByIdBffV2, generateParameterSetValueItemBffV2Api } from './bff';
import {
  getFormGeneratorIdByIndex,
  getParameterSetIdsByIndex,
  getTemplateIdByIndex,
  NUMBER_FORM_GENERATOR_FIELDS,
} from './commonUuids';

const NUMBER_OF_TEMPLATES = 2;

interface MockLogicalDevice {
  readonly id: string;
  readonly name: string;
  readonly role: EnumLogicalDeviceRoleApi;
}

// Two different logical devices, so the module computed response triggers the multi-merchant header select.
const MOCK_LOGICAL_DEVICES: readonly MockLogicalDevice[] = [
  { id: faker.string.uuid(), name: 'Merchant A - Terminal', role: 'PRIMARY' },
  { id: faker.string.uuid(), name: 'Merchant B - Terminal', role: 'SECONDARY' },
  { id: '373ef1df-1a3b-4e8b-a22c-9f55d5a740e3', name: 'Merchant C - Terminal', role: 'STANDALONE' },
];

interface MockParameterSetSnapshot {
  readonly parameterSetSnapshotId: string;
  readonly parameterSetId: string;
  readonly templateId: string;
  readonly logicalDevice: MockLogicalDevice;
}

// One entry per (template x logical device). Each template shares a single parameterSetSnapshotId across the
// logical devices (multi-merchant case). The ids are kept stable so the module computed response and the
// parameter set snapshot computed response always refer to each other.
const MOCK_PARAMETER_SET_SNAPSHOTS: readonly MockParameterSetSnapshot[] = faker.helpers
  .multiple((_, templateIndex) => templateIndex, { count: NUMBER_OF_TEMPLATES })
  .flatMap((templateIndex) => ({ templateIndex, parameterSetSnapshotId: faker.string.uuid() }))
  .flatMap(({ templateIndex, parameterSetSnapshotId }) =>
    MOCK_LOGICAL_DEVICES.map((logicalDevice) => ({
      parameterSetSnapshotId,
      // Same ids used by the existing configuration module mocks (commonUuids), so templates resolve the same way.
      parameterSetId: getParameterSetIdsByIndex(templateIndex),
      templateId: getTemplateIdByIndex(templateIndex),
      logicalDevice,
    })),
  );

function generateParameterSetSnapshotValues(): ParameterSetValueItemBffV2Api[] {
  return faker.helpers.multiple(
    (_, index) =>
      generateParameterSetValueItemBffV2Api({
        fieldId: getFormGeneratorIdByIndex(index),
        fieldValue: {
          single: faker.finance.accountName(),
          type: enumParameterSetFieldValueTypeV2.SMART_FIELD,
          smartField: { id: faker.string.uuid(), name: faker.person.fullName() },
          structure: [],
        },
      }),
    { count: NUMBER_FORM_GENERATOR_FIELDS },
  );
}

/**
 * Mock for `GET bff/deployment-units/{deploymentUnitId}/configuration-modules/snapshots/{snapshotId}/computed`.
 *
 * Parameter sets come with `parameterSetSnapshotId` + logical device data and WITHOUT `values`, which makes
 * `ConfigurationFormGeneratorService.resolveParameterSetSnapshotValues` call the parameter set snapshot endpoint.
 */
export function getComputedConfigurationModuleSnapshotByIdBffV2ResponseMock(
  snapshotId: string = faker.string.uuid(),
): ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api {
  const { parameterSets: _parameterSets, ...configurationModule } = generateConfigurationModuleByIdBffV2({
    id: snapshotId,
  });
  const parameterSets: ConfigurationModuleSnapshotResourceParameterSetReadBffV2Api[] = MOCK_PARAMETER_SET_SNAPSHOTS.map(
    ({ parameterSetSnapshotId, parameterSetId, templateId, logicalDevice }) => ({
      id: parameterSetId,
      templateId,
      templateName: `Template ${templateId.slice(0, 8)}`,
      parameterSetSnapshotId,
      logicalDeviceId: logicalDevice.id,
      logicalDeviceName: logicalDevice.name,
      logicalDeviceRoleSnapshot: logicalDevice.role,
    }),
  );
  return { ...configurationModule, parameterSets };
}

/**
 * Mock for `GET bff/deployment-units/{deploymentUnitId}/parameter-sets/snapshots/{parameterSetSnapshotId}/computed`.
 * Resolves the parameter set snapshot created by `getComputedConfigurationModuleSnapshotByIdBffV2ResponseMock`
 * and falls back to the `logicalDeviceId` query param for unknown snapshot ids.
 */
export function getComputedParameterSetSnapshotByIdBffV2ResponseMock(
  parameterSetSnapshotId: string,
  logicalDeviceId?: string,
): ParameterSetSnapshotComputedResourceReadBffV2Api {
  const snapshot =
    MOCK_PARAMETER_SET_SNAPSHOTS.find(
      (item) => item.parameterSetSnapshotId === parameterSetSnapshotId && item.logicalDevice.id === logicalDeviceId,
    ) ?? MOCK_PARAMETER_SET_SNAPSHOTS.find((item) => item.parameterSetSnapshotId === parameterSetSnapshotId);
  const logicalDevice =
    MOCK_LOGICAL_DEVICES.find(({ id }) => id === logicalDeviceId) ?? snapshot?.logicalDevice ?? MOCK_LOGICAL_DEVICES[0];
  const templateId = snapshot?.templateId ?? getTemplateIdByIndex(0);
  return {
    id: parameterSetSnapshotId,
    logicalDeviceId: logicalDeviceId ?? logicalDevice.id,
    logicalDeviceName: logicalDevice.name,
    logicalDeviceRoleSnapshot: logicalDevice.role,
    templateId,
    templateName: `Template ${templateId.slice(0, 8)}`,
    values: generateParameterSetSnapshotValues(),
  };
}

export function getComputedConfigurationModuleSnapshotByIdBffV2CustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const { snapshotId } = context.params;
  return HttpResponse.json(getComputedConfigurationModuleSnapshotByIdBffV2ResponseMock(String(snapshotId)));
}

export function getComputedParameterSetSnapshotByIdBffV2CustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const { parameterSetSnapshotId } = context.params;
  const logicalDeviceId = new URL(context.request.url).searchParams.get('logicalDeviceId') ?? undefined;
  return HttpResponse.json(
    getComputedParameterSetSnapshotByIdBffV2ResponseMock(String(parameterSetSnapshotId), logicalDeviceId),
  );
}
